import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { PlusIcon, PencilIcon, XMarkIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import { useAppDispatch } from '../../store';
import {
  selectRoleTypeError,
  selectRoleTypeLoading,
  selectedRoleTypes,
  fetchRoleTypes,
  createRoleType,
  editRoleType,
  resetForm,
  selectRoleTypePagination,
  toggleRoleTypeStatus,
} from './roleTypeSlice';
import { RoleType } from './roleType';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { usePermission } from '../../components/common/PermissionGuard';
import PageWrapper from '../../components/common/PageWrapper';
import SearchBar from '../../components/searchbar';
import Pagination from '../../components/common/Pagination';
import ChangeStatusConfirmation from "../../components/common/__tests__/ChangeStatusConfirmation";

const RoleTypes: React.FC = () => {
  const dispatch = useAppDispatch();
  const roleTypes = useSelector(selectedRoleTypes);
  const loading = useSelector(selectRoleTypeLoading);
  const error = useSelector(selectRoleTypeError);
  const pagination = useSelector(selectRoleTypePagination);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedRoleType, setSelectedRoleType] = useState<RoleType | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleName, setRoleName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [roleStatus, setRoleStatus] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedRoleTypeForStatus, setSelectedRoleTypeForStatus] = useState<RoleType | null>(null);

  useEffect(() => {
    dispatch(fetchRoleTypes({ page: currentPage, limit: itemsPerPage, search: searchTerm }));
  }, [dispatch, currentPage, itemsPerPage, searchTerm]);

  useEffect(() => {
    console.log('Role types state updated:', roleTypes);
  }, [roleTypes]);

  // Permissions - using master permissions for role types
  const canCreate = usePermission('master', 'create');
  const canUpdate = usePermission('master', 'update');
  const canDelete = usePermission('master', 'delete');
  const canRead = usePermission('master', 'read');

  const handleCreateRoleType = () => {
    dispatch(resetForm());
    setSelectedRoleType(null);
    setFormMode('create');
    setRoleName('');
    setDisplayName('');
    setRoleStatus(1);
    setIsFormOpen(true);
  };

  const handleEditRoleType = (roleType: RoleType) => {
    setRoleName(roleType.role_name);
    setDisplayName(roleType.display_name || roleType.displayName || '');
    setRoleStatus(roleType.status);
    setSelectedRoleType(roleType);
    setFormMode('edit');
    setIsFormOpen(true);
  };

  const handleFormSubmit = async () => {
    const formData = { 
      role_name: roleName, 
      display_name: displayName || roleName, // Use role_name as fallback if display_name is empty
      status: roleStatus 
    };
    console.log('Submitting form with data:', formData);
    console.log('selectedRoleType:', selectedRoleType);

    try {
      if (formMode === 'create') {
        console.log('Creating new role type');
        await dispatch(createRoleType(formData)).unwrap();
      } else if (selectedRoleType?.id) {
        console.log('Editing role type');
        await dispatch(editRoleType({ id: selectedRoleType.id, data: formData })).unwrap();
      }
      
      // Refresh the list to show updated data
      dispatch(fetchRoleTypes({ page: currentPage, limit: itemsPerPage, search: searchTerm }));
      
      setIsFormOpen(false);
      setRoleName('');
      setDisplayName('');
      setRoleStatus(1);
      setSelectedRoleType(null);
    } catch (error) {
      console.error('Failed to save role type:', error);
      // Error is handled by the slice, form stays open
    }
  };

  const toggleStatus = async (roleType: RoleType) => {
    const newStatus = roleType.status === 1 ? 0 : 1;
    try {
      await dispatch(toggleRoleTypeStatus({
        id: roleType.roleTypeId,
        status: newStatus
      })).unwrap();
      // Refresh the role types list after successful status update
      dispatch(fetchRoleTypes({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm
      }));
    } catch (error) {
      console.error('Failed to toggle status:', error);
    }
  };

  return (
    <PageWrapper>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Role Types Management</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manage role types for your application. All hardcoded role references will automatically use these role types.
            </p>
          </div>
          {canCreate && (
            <button
              onClick={handleCreateRoleType}
              className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create Role Type
            </button>
          )}
        </div>

        {/* Search */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <SearchBar
            placeholder="Search role types..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-600">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role Name (Code)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Display Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Updated On</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Updated By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {roleTypes?.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                        No role types found. {canCreate && 'Create your first role type to get started.'}
                      </td>
                    </tr>
                  ) : (
                    roleTypes?.map((roleType) => (
                      <tr key={roleType.id}>
                        <td className="px-6 py-4 text-sm font-medium text-gray-600">{roleType.role_name}</td>
                        <td className="px-6 py-4 text-sm font-medium">{roleType.display_name || roleType.displayName || roleType.role_name}</td>
                        <td className="px-6 py-4 text-sm">
                          {(() => {
                            // Show createdOn if record was never updated (bulk uploaded)
                            // Otherwise show updatedOn
                            const displayDate = roleType?.updatedOn &&
                              roleType?.createdOn &&
                              dayjs(roleType.updatedOn).isAfter(dayjs(roleType.createdOn))
                              ? roleType.updatedOn
                              : roleType?.createdOn || roleType?.updatedOn;
                            return displayDate ? dayjs(displayDate).format('DD/MM/YYYY HH:mm') : 'N/A';
                          })()}
                        </td>
                        <td className="px-6 py-4 text-sm">{roleType?.user_name || 'N/A'}</td>
                        <td className="px-6 py-4 text-sm cursor-pointer" onClick={() => {
                          setSelectedRoleTypeForStatus(roleType);
                          setIsStatusModalOpen(true);
                        }}>
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              roleType.status === 1
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {roleType.status === 1 ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right text-sm">
                          {canUpdate && (
                            <button
                              onClick={() => handleEditRoleType(roleType)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Form Modal */}
        {isFormOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="relative w-96 p-5 border shadow-lg rounded-md bg-white dark:bg-gray-800">
              <button
                onClick={() => setIsFormOpen(false)}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>

              <h3 className="text-lg font-medium mb-4">
                {formMode === 'create' ? 'Create Role Type' : 'Edit Role Type'}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Role Name (Code) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={roleName}
                    onChange={(e) => setRoleName(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter role name (e.g., marketing_team, super_admin)"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    This is the code value used in the system. Use lowercase with underscores (e.g., marketing_team).
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Display Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter display name (e.g., Marketing Team, Super Admin)"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    This is the user-friendly name shown in dropdowns and UI. Use proper capitalization (e.g., Marketing Team).
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setIsFormOpen(false)}
                  className="px-4 py-2 text-sm bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleFormSubmit}
                  className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md"
                >
                  {formMode === 'create' ? 'Create' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {isStatusModalOpen && selectedRoleTypeForStatus && (
        <ChangeStatusConfirmation
          open={isStatusModalOpen}
          onClose={() => {
            setIsStatusModalOpen(false);
            setSelectedRoleTypeForStatus(null);
          }}
          onConfirm={() => {
            toggleStatus(selectedRoleTypeForStatus);
            setIsStatusModalOpen(false);
            setSelectedRoleTypeForStatus(null);
          }}
          title="Change Status"
          message={`Are you sure you want to change status to ${selectedRoleTypeForStatus.status === 1 ? 'Inactive' : 'Active'}?`}
        />
      )}

      {pagination && pagination.total > 0 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.pages}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={(page) => {
            setCurrentPage(page);
            dispatch(fetchRoleTypes({ page, limit: itemsPerPage }));
          }}
          onItemsPerPageChange={(limit) => {
            setItemsPerPage(limit);
            setCurrentPage(1);
            dispatch(fetchRoleTypes({ page: 1, limit }));
          }}
          showItemsPerPage={true}
        />
      )}

    </PageWrapper>
  );
};

export default RoleTypes;

